import React from 'react';
import {
  PAYMENT_METHOD_OPTS,
  FLIGHT_PART_GROUPS,
  cancelFee,
  fmt,
  flightPartTotal,
  formatAmountInput,
  hasValue,
  num,
  parseAmountInput,
  supplierEffect,
} from './RevenueEventModal.helpers';
import {
  amountInput,
  deleteBtn,
  effectCell,
  farePartGrid,
  farePartInput,
  farePartLabel,
  farePartStack,
  farePartTotal,
  labelInput,
  paymentFieldInput,
  paymentFieldLabel,
  paymentFieldWrap,
  paymentWideCell,
  plainAmount,
  readOnlyInline,
  rowDeleteBtn,
  serviceLabelCaption,
  serviceLabelWrap,
  summaryItem,
  supplierName,
  warningText,
} from './RevenueEventModal.styles';

const PART_LABELS = ['운임', '유류', '공항세'];

function hasFlightPartInput(item, group) {
  return FLIGHT_PART_GROUPS[group].some((key) => hasValue(item?.[key]));
}

export function ServiceRow({ label, item, editableLabel, canRemove, onChange, onRemove }) {
  const hasRefundInput = item.item_type === 'flight' && hasFlightPartInput(item, 'refund');
  const hasAdditionalInput = item.item_type === 'flight' && hasFlightPartInput(item, 'additional');
  const effectiveItem = item.item_type === 'flight'
    ? {
      ...item,
      target_cost: flightPartTotal(item, 'target') || num(item.target_cost),
      supplier_refund: hasRefundInput ? flightPartTotal(item, 'refund') : null,
      additional_cost: hasAdditionalInput ? flightPartTotal(item, 'additional') : null,
    }
    : item;
  const fee = cancelFee(effectiveItem);
  return (
    <>
      <div style={supplierName}>
        <strong>{label}</strong>
        {editableLabel ? (
          <label style={serviceLabelWrap}>
            <span style={serviceLabelCaption}>{label === '항공' ? '구분 *' : '구분'}</span>
            <input
              value={item.item_label || ''}
              onChange={(e) => onChange('item_label', e.target.value)}
              placeholder={label === '호텔' ? '예: 3박' : '예: 성인 가는편'}
              style={labelInput}
            />
          </label>
        ) : (
          <span>
            {item.item_label || label}
            {item.is_legacy_hotel_value_moved && <small style={{ ...warningText, display: 'block', textAlign: 'left' }}>기존 입력값 1박 표시</small>}
          </span>
        )}
        {canRemove && <button type="button" style={rowDeleteBtn} onClick={onRemove}>삭제</button>}
      </div>
      {item.item_type === 'flight' ? (
        <FlightPartsReadOnly item={item} group="target" />
      ) : (
        <PlainAmount value={item.target_cost} />
      )}
      {item.item_type === 'flight' ? (
        <FlightPartsInput item={item} group="refund" onChange={onChange} />
      ) : (
        <AmountInput value={item.supplier_refund} onChange={(value) => onChange('supplier_refund', value)} />
      )}
      <div>
        <PlainAmount value={fee} danger={fee < 0} />
        {fee < 0 && <div style={warningText}>환불액 확인</div>}
      </div>
      {item.item_type === 'flight' ? (
        <FlightPartsInput item={item} group="additional" onChange={onChange} />
      ) : (
        <AmountInput value={item.additional_cost} onChange={(value) => onChange('additional_cost', value)} />
      )}
      <div style={effectCell}>{fmt(supplierEffect(effectiveItem))}</div>
    </>
  );
}

function FlightPartsReadOnly({ item, group }) {
  return (
    <div style={farePartStack}>
      <div style={farePartGrid}>
        {FLIGHT_PART_GROUPS[group].map((key, index) => (
          <div key={key}>
            <div style={farePartLabel}>{PART_LABELS[index]}</div>
            <PlainAmount value={item[key]} />
          </div>
        ))}
      </div>
      <div style={farePartTotal}>합계 {fmt(flightPartTotal(item, group) || num(item.target_cost))}</div>
    </div>
  );
}

function FlightPartsInput({ item, group, onChange }) {
  return (
    <div style={farePartStack}>
      <div style={farePartGrid}>
        {FLIGHT_PART_GROUPS[group].map((key, index) => (
          <label key={key} style={{ display: 'grid', gap: 2, minWidth: 0 }}>
            <span style={farePartLabel}>{PART_LABELS[index]}</span>
            <input
              type="text"
              inputMode="text"
              pattern="-?[0-9,]*"
              value={formatAmountInput(item[key])}
              onFocus={(e) => e.target.select()}
              onChange={(e) => onChange(key, parseAmountInput(e.target.value))}
              style={farePartInput}
            />
          </label>
        ))}
      </div>
      <div style={farePartTotal}>합계 {fmt(flightPartTotal(item, group))}</div>
    </div>
  );
}

export function Summary({ label, value, strong }) {
  return (
    <div style={summaryItem}>
      <span>{label}</span>
      <strong style={{ color: num(value) < 0 ? '#dc2626' : 'var(--text)', fontSize: strong ? 15 : 13 }}>{fmt(value)}</strong>
    </div>
  );
}

export function AmountInput({ value, onChange }) {
  return (
    <input
      type="text"
      inputMode="text"
      pattern="-?[0-9,]*"
      value={formatAmountInput(value)}
      onFocus={(e) => e.target.select()}
      onChange={(e) => onChange(parseAmountInput(e.target.value))}
      style={amountInput}
    />
  );
}

export function PlainAmount({ value, danger }) {
  return <div style={{ ...plainAmount, color: danger ? '#dc2626' : plainAmount.color }}>{fmt(value)}</div>;
}

export function ReadOnlyInline({ label, value }) {
  return <div style={readOnlyInline}><span>{label}</span><strong>{fmt(value)}</strong></div>;
}

function PaymentField({ label, value, onChange, type = 'text', options }) {
  const isAmount = type === 'amount';
  return (
    <label style={paymentFieldWrap}>
      <span style={paymentFieldLabel}>{label}</span>
      {options ? (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          style={paymentFieldInput}
        >
          <option value="">선택</option>
          {options.map((option) => <option key={option.value ?? option} value={option.value ?? option}>{option.label ?? option}</option>)}
        </select>
      ) : (
        <input
          type={isAmount ? 'text' : type}
          inputMode={isAmount ? 'text' : undefined}
          pattern={isAmount ? '-?[0-9,]*' : undefined}
          value={isAmount ? formatAmountInput(value) : (value ?? '')}
          onFocus={(e) => {
            if (isAmount) e.target.select();
          }}
          onChange={(e) => onChange(isAmount ? parseAmountInput(e.target.value) : (e.target.value || null))}
          style={paymentFieldInput}
        />
      )}
    </label>
  );
}

export function PaymentRow({ payment, index, onChange, onRemove, style }) {
  return (
    <div style={style}>
      <PaymentField label="지불일" type="date" value={payment.payment_date} onChange={(value) => onChange(index, 'payment_date', value)} />
      <PaymentField label="금액" type="amount" value={payment.amount} onChange={(value) => onChange(index, 'amount', value)} />
      <PaymentField label="지불수단" value={payment.payment_method} onChange={(value) => onChange(index, 'payment_method', value)} options={PAYMENT_METHOD_OPTS} />
      {payment.payment_method === 'cash' ? (
        <>
          <PaymentField label="현금영수증번호" value={payment.cash_receipt_issue_no} onChange={(value) => onChange(index, 'cash_receipt_issue_no', value)} />
          <PaymentField label="현금영수증금액" type="amount" value={payment.cash_receipt_amount} onChange={(value) => onChange(index, 'cash_receipt_amount', value)} />
        </>
      ) : (
        <div style={paymentWideCell}>
          <PaymentField label="카드입금 메모" value={payment.card_deposit_memo} onChange={(value) => onChange(index, 'card_deposit_memo', value)} />
        </div>
      )}
      <button type="button" style={deleteBtn} onClick={() => onRemove(index)}>삭제</button>
    </div>
  );
}

export function RefundRow({ refund, index, onChange, onRemove, style }) {
  return (
    <div style={style}>
      <PaymentField label="환불일" type="date" value={refund.refund_date} onChange={(value) => onChange(index, 'refund_date', value)} />
      <PaymentField label="금액" type="amount" value={refund.amount} onChange={(value) => onChange(index, 'amount', value)} />
      <PaymentField label="환불수단" value={refund.refund_method} onChange={(value) => onChange(index, 'refund_method', value)} options={PAYMENT_METHOD_OPTS} />
      {refund.refund_method === 'cash' ? (
        <>
          <PaymentField label="현영 취소승인번호" value={refund.cash_receipt_cancel_approval_no} onChange={(value) => onChange(index, 'cash_receipt_cancel_approval_no', value)} />
          <PaymentField label="현영 취소금액" type="amount" value={refund.cash_receipt_cancel_amount} onChange={(value) => onChange(index, 'cash_receipt_cancel_amount', value)} />
        </>
      ) : (
        <>
          <PaymentField label="카드취소/메모" value={refund.memo} onChange={(value) => onChange(index, 'memo', value)} />
          <div />
        </>
      )}
      <PaymentField label="환불계좌/메모" value={refund.refund_account} onChange={(value) => onChange(index, 'refund_account', value)} />
      <button type="button" style={deleteBtn} onClick={() => onRemove(index)}>삭제</button>
    </div>
  );
}
